import React, { useMemo } from "react";
import { Alert, Container } from "reactstrap";
import DateText from "~/components/DateText";
import useApi from "~/hooks/use-api";

export default function AlertsPage() {
  const { data: alerts } = useApi<{
    data: {
      resultType: "resultType";
      result: { metric: Record<string, string>; value: [number, number] }[];
    };
    result: "success";
  }>(
    `/api/metrics/query?query=${encodeURIComponent(
      `ALERTS{alertstate="firing"} * ignoring (alertstate) ALERTS_FOR_STATE`
    )}`
  );

  const aggregatedAlerts = useMemo(() => {
    const alertMap = new Map<
      string,
      { metric: Record<string, string>; value: [number, number] }[]
    >();

    if (!alerts) return alertMap;

    for (const alert of alerts.data.result) {
      const alertName = alert.metric.alertname;
      if (!alertMap.has(alertName)) {
        alertMap.set(alertName, [alert]);
      } else {
        alertMap.get(alertName)?.push(alert);
      }
    }

    return alertMap;
  }, [alerts]);

  return (
    <Container className="mt-4">
      <h1>Alerts</h1>

      <div className="py-3 border-top">
        {aggregatedAlerts.size === 0 ? (
          <p>No active alerts.</p>
        ) : (
          Array.from(aggregatedAlerts).map(([alertName, alerts]) => (
            <AlertDetails
              key={alertName}
              alertName={alertName}
              alerts={alerts}
            />
          ))
        )}
      </div>
    </Container>
  );
}

function AlertDetails({
  alertName,
  alerts,
}: {
  alertName: string;
  alerts: { metric: Record<string, string>; value: [number, number] }[];
}) {
  const common = useMemo(() => commonLabels(alerts), [alerts]);

  return (
    <Alert color="danger">
      <h2>{alertName}</h2>
      <p>
        {common
          .filter((label) => label !== "alertname")
          .map((label) => (
            <span key={label}>
              <strong>{label}=</strong>
              &quot;{alerts[0].metric[label]}&quot;{" "}
            </span>
          ))}
      </p>

      {alerts.length === 1 ? (
        <p>
          <strong>Since:</strong>{" "}
          <DateText
            date={new Date(alerts[0].value[1] * 1000).toISOString()}
            format="datetime"
          />
        </p>
      ) : (
        <details>
          <summary>Instances</summary>

          {alerts.map((alert, index) => (
            <p key={index}>
              {Object.entries(alert.metric).map(([key, value]) =>
                common.includes(key) ? null : (
                  <span key={key}>
                    <strong>{key}=</strong>
                    &quot;{value}&quot;{" "}
                  </span>
                )
              )}
              <strong>Since:</strong>{" "}
              <DateText
                date={new Date(alert.value[1] * 1000).toISOString()}
                format="datetime"
              />
            </p>
          ))}
        </details>
      )}
    </Alert>
  );
}

function commonLabels(alerts: { metric: Record<string, string> }[]): string[] {
  if (alerts.length === 0) return [];

  const common = { ...alerts[0].metric };

  for (const alert of alerts) {
    for (const key in common) {
      if (!(key in alert.metric)) {
        delete common[key];
      }

      if (common[key] !== alert.metric[key]) {
        delete common[key];
      }
    }
  }

  return Object.keys(common).sort();
}
